import { axiosPrivate } from "../services/makeRequest"
import { useEffect, useRef } from "react"
import useRefreshToken from "./useRefreshToken"
import { useAuth } from "../contexts/AuthContext"

let refreshPromise = null // Shared across all hook instances

export default function useAxiosPrivate() {
    const refreshMutation = useRefreshToken()
    const { auth } = useAuth()
    const token = auth?.token
    const effectRan = useRef(false)

    useEffect(() => {
        if (effectRan.current) return

        const requestIntercept = axiosPrivate.interceptors.request.use(
            (config) => {
                if (
                    !config.headers["authorization"] &&
                    !config.headers["Authorization"]
                ) {
                    config.headers["authorization"] = `Bearer ${token}`
                }
                return config
            },
            (error) => Promise.reject(error),
        )

        const responseIntercept = axiosPrivate.interceptors.response.use(
            (response) => response,
            async (error) => {
                const prevRequest = error?.config
                if (error?.response?.status === 403 && !prevRequest?.sent) {
                    prevRequest.sent = true
                    // If a refresh is already in progress, wait for it
                    if (!refreshPromise) {
                        refreshPromise = refreshMutation
                            .mutateAsync()
                            .then((newAccessToken) => {
                                refreshPromise = null
                                return newAccessToken
                            })
                            .catch((err) => {
                                refreshPromise = null
                                throw err
                            })
                    }
                    try {
                        const newAccessToken = await refreshPromise
                        prevRequest.headers["authorization"] =
                            `Bearer ${newAccessToken}`
                        return axiosPrivate(prevRequest)
                    } catch (err) {
                        return Promise.reject(err)
                    }
                }
                return Promise.reject(error)
            },
        )

        effectRan.current = true

        return () => {
            axiosPrivate.interceptors.request.eject(requestIntercept)
            axiosPrivate.interceptors.response.eject(responseIntercept)
        }
    }, [token, refreshMutation])

    return axiosPrivate
}
